import { scaleLinear } from 'd3-scale';

import { PriceRange } from '~/types/axis.types';
import { CreateChartOptions, XAxis, YAxis } from '~/types/create.types';
import { TsDataPoint } from '~/types/timeseries.types';
import { calculateXAxisTicks } from '~/utils/x-scale';
import { baseYAxisValues, calculateYAxisTicks } from '~/utils/y-scale/y-scale';

interface XAxisProps {
  data: TsDataPoint[];
  options: CreateChartOptions;
  domain: [number, number];
  barSpacing?: number;
}

export const xAxis = ({
  data,
  domain,
  options,
  barSpacing,
}: XAxisProps): XAxis => {
  const spaceBetweenTicks =
    barSpacing ?? (options.width - options.gutters.y) / data.length;
  const xAxisTicks = calculateXAxisTicks({
    data,
    domain,
    spaceBetweenTicks: spaceBetweenTicks,
  });
  const latestTickIndex = xAxisTicks.ticks.slice(-1)[0].index;

  return {
    barSpacing: spaceBetweenTicks,
    latestTickIndex: latestTickIndex,
    axis: xAxisTicks,
    xScale: scaleLinear()
      .domain([domain[0], domain[1]])
      .range([0, options.width - options.gutters.y]),
  };
};

interface YAxisProps {
  data: TsDataPoint[];
  options: CreateChartOptions;
  dataRange?: PriceRange;
  minDomainValue?: number;
}

export const yAxis = ({
  data,
  options,
  dataRange,
  minDomainValue = -Infinity,
}: YAxisProps): YAxis => {
  const baseYAxisSetup = baseYAxisValues(data, options, dataRange);
  const yAxisTicks = calculateYAxisTicks(baseYAxisSetup);
  const padding =
    (baseYAxisSetup.dataRange.max - baseYAxisSetup.dataRange.min) * 0.07;
  const min = Math.max(baseYAxisSetup.dataRange.min - padding, minDomainValue);
  const max = baseYAxisSetup.dataRange.max + padding;

  return {
    ticks: yAxisTicks,
    yScale: scaleLinear()
      .domain([min, max])
      .range([options.height - options.gutters.x, 0]),
  };
};
